import React, { useState, useEffect, useRef, useContext } from "react";
import { usePopup } from "components/common/wrappers/withPopup";
import { BreakpointContext, Loading } from "components/common";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { boostProductTierConfig, boostTiers } from "@tomocredit/common-fe/consts";
import { calculatePriceAfterPromotion, formatCurrency } from "@tomocredit/common-fe/utils";
import axios from "axios";
import BoostContainer from "pages/Boost/components/BoostContainer";
import CheckoutForm from "./components/CheckoutForm";

const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Enable the skeleton loader UI for the optimal loading experience.
const loader = "auto";

const appearance = {
  theme: "stripe",
  labels: "floating", // float labels
  variables: {
    fontFamily: "Gilroy, sans-serif",
    spacingUnit: "3px", // spacing inside input
    spacingAccordionItem: "30px", // spacing between accordion
    spacingGridRow: "15px", // spacing between input row
  },
  rules: {
    ".Input::placeholder": {
      color: "#718096", // text-gray-600
    },
    ".Label": {
      color: "#718096", // text-gray-600
    },
    ".AccordionItem": {
      padding: "20px", // spacing inside accordion
    },
  },
};

// todo: load price and subscription from backend
const options = {
  mode: "subscription",
  amount: 999,
  currency: "usd",
  appearance,
  loader,
  // paymentMethodTypes: ["card", "cashapp", "link"],
};

const buildOptions = (tier, term, credit = 0, percentOff = 0) => {
  const selectTierConfig = boostProductTierConfig[term]?.[tier];
  const { price } = selectTierConfig || {};
  const priceAfterPromotion = calculatePriceAfterPromotion(
    price,
    percentOff,
    credit,
  );
  return {
    ...options,
    amount: Math.round(priceAfterPromotion * 100),
  };
};

const CheckoutPage = () => {
  // get email from query params
  // if email not exist in query param, send user to enter email first before checkout
  const queryParameters = new URLSearchParams(window.location.search);
  const emailRaw = queryParameters.get("email");
  const tierRaw = queryParameters.get("tier");
  const termRaw = queryParameters.get("term");
  const tierFromQueryParam = tierRaw in boostTiers ? tierRaw : null;
  const emailFromQueryParam =
    emailRaw && typeof emailRaw === "string" && emailRaw.trim().toLowerCase();
  const termFromQueryParam = ["yearly", "monthly"].includes(termRaw)
    ? termRaw
    : null;

  // alternatively can get from url params

  const [formEmail, setFormEmail] = useState("");
  const [email, setEmail] = useState(emailFromQueryParam || formEmail);

  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md"); // 768

  const clientSecret = queryParameters.get("payment_intent_client_secret");
  const paymentIntent = queryParameters.get("payment_intent");
  const referralCode = queryParameters.get("referralCode") || "";
  const initialScrollToCheckout = queryParameters.get("checkout") === "true";

  const [message, setMessage] = useState("");
  const [checkoutTier, setCheckoutTier] = useState(
    tierFromQueryParam || boostTiers.VIP,
  );

  /* TC-3456 Monthly Only */
  const isMonthlyOnlyTesting = false;
  const testTerm = (isMonthlyOnlyTesting && "monthly") || null;

  const [checkoutTerm, setCheckoutTerm] = useState(
    testTerm || termFromQueryParam || "monthly",
  );
  const [scrollToCheckout, setScrollToCheckout] = useState(
    initialScrollToCheckout,
  );
  const [verifyingPayment, setVerifyingPayment] = useState(!!clientSecret);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [promotionCredit, setPromotionCredit] = useState(0);
  const [promotionPercentOff, setPromotionPercentOff] = useState(0);

  // checkout form ref
  const checkoutFormRef = useRef(null);
  const checkoutFormContainerRef = useRef(null);
  const errorMessageRef = useRef(null);

  // always keep our email up to date
  useEffect(() => {
    setEmail(emailFromQueryParam || formEmail);
  }, [emailFromQueryParam || formEmail]);

  const { hideExitPopup } = usePopup();

  // has payment intent  -> check payment status
  useEffect(() => {
    if (verifyingPayment) {
      const verifyPayment = async () => {
        let verifyPaymentRes;
        try {
          verifyPaymentRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/boost/verifyStripePaymentAndEnroll`,
            { email, clientSecret, paymentIntent },
          );
        } catch (err) {
          const errorMessage = err?.response?.data?.userMessage;
          setMessage(errorMessage || "Error connecting to server");
          setVerifyingPayment(false);
          return;
        }

        const paymentVerified = verifyPaymentRes?.data?.status === "success";
        if (paymentVerified) {
          hideExitPopup();

          const redirectToStripe = () => {
            window.location.assign(
              `${
                process.env.REACT_APP_DOMAIN
              }/dashboard/stripe-redirect?email=${encodeURIComponent(
                email,
              )}&payment_intent=${
                paymentIntent.id
              }&payment_intent_client_secret=${clientSecret}&redirect_status=${
                paymentIntent.status
              }`,
            );
          };

          const { amount } =
            buildOptions(
              checkoutTier,
              checkoutTerm,
              promotionCredit,
              promotionPercentOff,
            ) || {};
          const formattedAmount = formatCurrency(amount / 100, {
            currencyDisplay: "none",
            digits: 2,
          });
          window.fbq?.("track", "Subscribe", {
            email,
            currency: "USD",
            value: formattedAmount,
          });
          window.dataLayer?.push({
            event: "Subscribe",
            conversionValue: formattedAmount,
            email,
            tier: checkoutTier,
            term: checkoutTerm,
          });
          if (window?.gtag) {
            window?.gtag("event", "boost_checkout_payment_verified", {
              email,
              tier: checkoutTier,
              term: checkoutTerm,
              amount: formattedAmount,
              event_callback: redirectToStripe,
            });
          } else {
            redirectToStripe();
          }
        } else {
          setMessage("Unable to verify payment status");
          setVerifyingPayment(false);
        }
      };
      verifyPayment();
    }
  }, [clientSecret, email, paymentIntent, verifyingPayment]);

  // scroll to checkout form on tier selection
  useEffect(() => {
    if (
      scrollToCheckout &&
      checkoutTier in boostProductTierConfig[checkoutTerm]
    ) {
      checkoutFormContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        // block: "end",
        inline: "center",
      });
    }
    return () => setScrollToCheckout(false);
  }, [checkoutTier, scrollToCheckout]);

  // scroll to error on redirect
  useEffect(() => {
    if (message && stripeLoaded) {
      errorMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        // block: "end",
        inline: "center",
      });
    }
  }, [message, stripeLoaded]);

  // erase email on back button (back to previous state with no email param)
  useEffect(() => {
    if (!emailFromQueryParam) {
      setFormEmail("");
    }
  }, [emailFromQueryParam]);

  // update tier param on tier selection
  useEffect(() => {
    if (checkoutTier) {
      const url = new URL(window.location);
      url.searchParams.set("tier", checkoutTier);
      window.history.replaceState(null, "", url.toString());
    }
    if (checkoutTerm) {
      const url = new URL(window.location);
      url.searchParams.set("term", checkoutTerm);
      window.history.replaceState(null, "", url.toString());
    }
    if (checkoutTier && checkoutTerm) {
      window?.gtag?.("event", "boost_checkout_product_selected", {
        tier: checkoutTier,
        term: checkoutTerm,
        email,
      });
    }
  }, [checkoutTier, checkoutTerm]);

  // user coming back from payment -> render loading until verifyStripePayment completed
  if (verifyingPayment && clientSecret) {
    return <Loading showBackdrop boost />;
  }
  // render payment form
  return (
    <div>
      <BoostContainer relative disablePopup disableFooter background="pricing">
        <div style={{ minWidth: 350 }}>
          <div
            className="flex justify-center items-center min-h-screen w-full"
            ref={checkoutFormContainerRef}
          >
            <Elements
              stripe={stripe}
              options={buildOptions(
                checkoutTier,
                checkoutTerm,
                promotionCredit,
                promotionPercentOff,
              )}
            >
              <CheckoutForm
                email={email}
                productTier={checkoutTier}
                productTerm={checkoutTerm}
                setCheckoutTerm={setCheckoutTerm}
                setCheckoutTier={setCheckoutTier}
                message={message}
                setMessage={setMessage}
                setStripeLoaded={setStripeLoaded}
                innerRef={checkoutFormRef}
                errorMessageRef={errorMessageRef}
                belowMd={belowMd}
                referralCode={referralCode}
                promotionCredit={promotionCredit}
                setPromotionCredit={setPromotionCredit}
                promotionPercentOff={promotionPercentOff}
                setPromotionPercentOff={setPromotionPercentOff}
              />
            </Elements>
          </div>
        </div>
      </BoostContainer>
    </div>
  );
};

export default CheckoutPage;
